import { WebApp } from "@/shared/telegram"

export interface StartParams {
  chatId: number | null
  devMode: boolean
  tabParam: string | null
  reportId: string | null
  captchaMode: boolean
  joinRequestAttemptId: string | null
  userId: number | null
  autoMessageId: number | null
  shareToken: string | null
  onboardingButton: string | null
  cloneRedirect: boolean
  adSource: string | null
}

// Telegram supergroup/channel ids always start with -100 and are negative int64;
// require at least one digit after the prefix so we reject the bare "-100" sentinel
// alongside non-integers (NaN, floats, Infinity).
export const parseChatId = (value: string): number | null => {
  if (!/^-100\d+$/.test(value)) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) ? parsed : null
}

// Telegram user ids are always positive int64; reject NaN, floats, Infinity,
// and 0/negatives.
const parseUserId = (value: string): number | null => {
  const parsed = Number(value)
  if (!Number.isInteger(parsed)) return null
  return parsed > 0 ? parsed : null
}

const AD_SOURCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
// Nine digits in a row are shaped like a Telegram user or chat id; mirrors domain/entities/usage.py.
const ID_DIGITS_PATTERN = /[0-9]{9}/

const parseAdSource = (value: string): string | null =>
  AD_SOURCE_PATTERN.test(value) && !ID_DIGITS_PATTERN.test(value) ? value : null

const assignParam = (result: StartParams, key: string, value: string): void => {
  switch (key) {
    case "chat_id": {
      const parsed = parseChatId(value)
      if (parsed !== null) result.chatId = parsed
      break
    }
    case "dev_mode":
      result.devMode = value === "True" || value === "true"
      break
    case "tab":
      result.tabParam = value
      break
    case "report_id":
      result.reportId = value
      break
    case "captcha":
      result.captchaMode = value === "true"
      break
    case "join_request_attempt_id":
      if (/^[a-f0-9]{16}$/.test(value)) result.joinRequestAttemptId = value
      break
    case "user_id": {
      const parsed = parseUserId(value)
      if (parsed !== null) result.userId = parsed
      break
    }
    case "auto_message_id": {
      const parsed = Number(value)
      if (/^-?\d+$/.test(value) && Number.isSafeInteger(parsed) && parsed !== 0) result.autoMessageId = parsed
      break
    }
    case "share_token":
      result.shareToken = value
      break
    case "ob":
      result.onboardingButton = value
      break
    case "clone_redirect":
      result.cloneRedirect = value === "1"
      break
    case "src":
      result.adSource = parseAdSource(value)
      break
    default:
      break
  }
}

export const parseStartParams = (): StartParams => {
  const startApp = WebApp.initDataUnsafe.start_param

  const result: StartParams = {
    chatId: null,
    devMode: false,
    tabParam: null,
    reportId: null,
    captchaMode: false,
    joinRequestAttemptId: null,
    userId: null,
    autoMessageId: null,
    shareToken: null,
    onboardingButton: null,
    cloneRedirect: false,
    adSource: null
  }

  if (!startApp) return result

  const segments = startApp.split("==").filter(Boolean)

  segments.forEach(segment => {
    if (!segment.includes("=")) {
      if (result.chatId === null) {
        const parsed = parseChatId(segment)
        if (parsed !== null) result.chatId = parsed
      }
      return
    }

    const [key, ...rawValue] = segment.split("=")
    const value = rawValue.join("=")

    if (key && value) {
      assignParam(result, key, value)
    }
  })

  return result
}
